import {
  LoadForm,
  updateFormDefinitionVariables,
} from "types/gql-types";
import { getApolloErrorMessage } from "utils/apollo";
import { validateContent } from "./toml-validator";

const FORM_PUBLISH_ERROR = "Error Publishing the Form";

export type SubmitFormResult = { saved: true } | { saved: false };

type SnackbarOptions = { message: string; status: "success" | "error" };

type SaveDraft = {
  enabled: boolean;
  content: string;
  form?: { open?: boolean } | null;
};

const verifyFormDefinitionToml = (data: SaveDraft): boolean => {
  if (!data.enabled) return true;
  return validateContent(data.content).length === 0;
};

export const saveFormDefinitionUpdate = async <T extends SaveDraft>(opts: {
  id: string;
  formData: T;
  updateForm: (options: { variables: updateFormDefinitionVariables }) => Promise<unknown>;
  openSnackbar: (options: SnackbarOptions) => void;
  logError: (err: Error) => void;
  cast: (data: T) => T;
}): Promise<SubmitFormResult> => {
  if (!verifyFormDefinitionToml(opts.formData)) {
    opts.openSnackbar({ message: "Update failed, TOML invalid", status: "error" });
    return { saved: false };
  }

  try {
    const { form: formInfo, ...updatedData } = opts.cast({ ...opts.formData });
    const dataToLoad = { formOpen: formInfo && formInfo.open, ...updatedData };
    await opts.updateForm({ variables: { id: opts.id, data: dataToLoad } });
    return { saved: true };
  } catch (err) {
    opts.logError(err as Error);
    throw err;
  }
};

export const saveAndPublishFormDefinition = async (opts: {
  submitForm: () => Promise<SubmitFormResult | void>;
  loadForm: (options: { variables: { id: string } }) => Promise<{ data?: LoadForm | null }>;
  formDefinitionId: string;
  setFieldValue: (field: string, value: unknown) => unknown | Promise<unknown>;
  openSnackbar: (options: SnackbarOptions) => void;
}): Promise<void> => {
  try {
    const result = await opts.submitForm();
    if (!result?.saved) return;

    const { data } = await opts.loadForm({
      variables: { id: opts.formDefinitionId },
    });
    const published = data?.loadFormDefinition;
    if (published) {
      await opts.setFieldValue("formId", published.formId);
      await opts.setFieldValue("form", published.form);
    }

    opts.openSnackbar({ message: "Published", status: "success" });
  } catch (err) {
    opts.openSnackbar({
      message: getApolloErrorMessage(err, FORM_PUBLISH_ERROR),
      status: "error",
    });
  }
};
